// /src/hooks/useScenarioActions.js
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { trackPromise } from "react-promise-tracker";
import { useTracking } from "react-tracking";

import {
	createScenario,
	updateScenario,
	deleteScenario,
	copyScenario,
	importScenario,
	updateScenarioStatus,
	dumpOneScenario,
} from "../slices/scenarioSlice";
import { importBaseline } from "../slices/baselineSlice";

import { useDialogBox } from "../hooks/useDialogBox";
import { useScenariosData } from "../hooks/useScenariosData";

import { exportJSON, exportSpreadsheet } from "../utils/fileUtils";
import { Session } from "../utils/sessionUtils";
import { debugLog, errorLog } from "../utils/debugUtils";

export function useScenarioActions({
	fetchScenarioByIdOrUuid,
	fetchScenarios,
	clearDataError,
	handleBackToList,
	localScenario,
	activeStep,
}) {
	const dispatch = useDispatch();
	const { trackEvent } = useTracking();
	const { showDialog, withError } = useDialogBox();
	const { fetchScenarioForExport } = useScenariosData();

	// CREATE
	const createScenarioAction = useCallback(() => {
		debugLog(3, "[useScenarioActions][ACTION] createScenarioAction", {
			localScenario: localScenario,
		});
		debugLog(2, "[useScenarioActions][FLOW] createScenario started", {});

		trackPromise(
			dispatch(createScenario({ data: localScenario }))
				.unwrap()
				.then((response) => {
					debugLog(3, "[useScenarioActions][STATE] scenario created", { response: response });

					showDialog(
						{
							title: "Successful",
							message: ["The Scenario was created successfully!"],
						},
						() => {
							fetchScenarioByIdOrUuid(response.id);
							clearDataError && clearDataError();
						},
					);

					trackEvent({ event: "createScenario", props: { to: localScenario } });
				})
				.catch((error) => {
					errorLog(2, "[useScenarioActions][FLOW] createScenario failed", { error: error });
					console.error("createScenario failed", error);
					// Create is a mutation, so keep the shared error contract while
					// intentionally skipping retry to avoid duplicate writes.
					withError(error, null, {
						fallbackMessage: "Scenario create failed.",
					});
				}),
		);
	}, [
		dispatch,
		localScenario,
		fetchScenarioByIdOrUuid,
		clearDataError,
		showDialog,
		trackEvent,
		withError,
	]);

	// UPDATE
	const updateScenarioAction = useCallback(() => {
		debugLog(3, "[useScenarioActions][ACTION] updateScenarioAction", {
			localScenario: localScenario,
			activeStep: activeStep,
		});
		debugLog(2, "[useScenarioActions][FLOW] updateScenario started", {
			id: localScenario?.id,
		});

		const scenarioForSave = JSON.parse(JSON.stringify(localScenario));
		debugLog(4, "[useScenarioActions][PROCESS] scenarioForSave object", {
			scenarioForSave: scenarioForSave,
		});

		trackPromise(
			dispatch(updateScenario({ id: scenarioForSave.id, data: scenarioForSave }))
				.unwrap()
				.then((response) => {
					debugLog(3, "[useScenarioActions][STATE] scenario updated", {
						response: response,
						step: activeStep,
					});

					showDialog(
						{
							title: "Successful",
							message: [`The Scenario (Step #${activeStep + 1}) was updated successfully!`],
						},
						() => {
							fetchScenarioByIdOrUuid(scenarioForSave.id);
							clearDataError && clearDataError();
						},
					);

					trackEvent({
						event: `updateScenario`,
						props: { to: scenarioForSave },
					});
				})
				.catch((error) => {
					errorLog(2, "[useScenarioActions][FLOW] updateScenario failed", {
						id: scenarioForSave?.id,
						error: error,
					});
					console.error("updateScenario failed", error);
					// Update is a mutation, so keep the shared contract but avoid
					// retry; a server-side save may already have completed.
					withError(error, null, {
						fallbackMessage: "Scenario update failed.",
					});
				}),
		);
	}, [
		dispatch,
		localScenario,
		activeStep,
		fetchScenarioByIdOrUuid,
		clearDataError,
		trackEvent,
		showDialog,
		withError,
	]);

	// DELETE - purge
	const deleteScenarioAction = useCallback(
		(id) => {
			debugLog(3, "[useScenarioActions][ACTION] deleteScenarioAction", { id: id });
			debugLog(2, "[useScenarioActions][FLOW] deleteScenario started", { id: id });

			trackPromise(
				dispatch(deleteScenario({ data: { id, updatedBy: Session().currUser.id } }))
					.unwrap()
					.then(() => {
						debugLog(3, "[useScenarioActions][STATE] scenario deleted", { id: id });

						showDialog(
							{
								title: "Successful",
								message: ["The Scenario was purged successfully."],
							},
							() => handleBackToList && handleBackToList(),
						);

						trackEvent({ event: `Scenario (#${id}) was purged`, props: null });
					})
					.catch((error) => {
						errorLog(2, "[useScenarioActions][FLOW] deleteScenario failed", {
							id: id,
							error: error,
						});
						console.error("deleteScenario failed", error);
						// Delete is a mutation, so keep the shared contract but avoid
						// retry; the purge may already have succeeded server-side.
						withError(error, null, {
							fallbackMessage: "Scenario delete failed.",
						});
					}),
			);
		},
		[dispatch, showDialog, handleBackToList, trackEvent, withError],
	);

	// COPY
	const copyScenarioAction = useCallback(
		(id) => {
			debugLog(3, "[useScenarioActions][ACTION] copyScenarioAction", { id: id });
			debugLog(2, "[useScenarioActions][FLOW] copyScenario started", { id: id });

			trackPromise(
				dispatch(copyScenario({ data: { id, updatedBy: Session().currUser.id } }))
					.unwrap()
					.then((response) => {
						const copiedId = response?.newId ?? response?.id ?? response?.scenario?.id;
						if (!copiedId) {
							throw new Error("Scenario copy response missing newId.");
						}
						debugLog(3, "[useScenarioActions][STATE] scenario copied", {
							id: id,
							newId: copiedId,
							response: response,
						});

						showDialog(
							{
								title: "Successful",
								message: ["The Scenario was duplicated successfully."],
							},
							() => handleBackToList && handleBackToList(),
						);

						trackEvent({
							event: `Scenario was copied (#${id} to #${copiedId})`,
							props: null,
						});
					})
					.catch((error) => {
						errorLog(2, "[useScenarioActions][FLOW] copyScenario failed", {
							id: id,
							error: error,
						});
						console.error("copyScenario failed", error);
						// Copy is also a mutation, so do not offer retry after a failed
						// response; the duplicate scenario may already exist.
						withError(error, null, {
							fallbackMessage: "Scenario copy failed.",
						});
					}),
			);
		},
		[dispatch, showDialog, handleBackToList, trackEvent, withError],
	);

	// STATUS CHANGE - archive, restore and sfot delete
	const updateStatusAction = useCallback(
		(id, newStatus, showMsgBox = true) => {
			debugLog(3, "[useScenarioActions][ACTION] updateStatusAction", {
				id: id,
				newStatus: newStatus,
				showMsgBox: showMsgBox,
			});
			debugLog(2, "[useScenarioActions][FLOW] updateScenarioStatus started", {
				id: id,
				newStatus: newStatus,
			});

			let strAction = "archived";
			if (newStatus === 1) strAction = "re-activated";
			else if (newStatus === -1) strAction = "deleted";

			trackPromise(
				dispatch(
					updateScenarioStatus({
						id,
						status_id: newStatus,
						updatedBy: Session().currUser.id,
					}),
				)
					.unwrap()
					.then(() => {
						debugLog(3, "[useScenarioActions][STATE] scenario status updated", {
							id: id,
							newStatus: newStatus,
							action: strAction,
						});

						if (showMsgBox) {
							showDialog(
								{
									title: "Successful",
									message: [
										`The Scenario "${localScenario.name}" status ${strAction} successfully.`,
									],
								},
								() => {
									if (newStatus === 1) fetchScenarioByIdOrUuid(localScenario.id);
									else handleBackToList && handleBackToList();
								},
							);
						} else {
							if (newStatus === 1) fetchScenarioByIdOrUuid(id);
							else handleBackToList && handleBackToList();
						}

						trackEvent({
							event: `Scenario (#${id}) was ${strAction}`,
							props: null,
						});
					})
					.catch((error) => {
						errorLog(2, "[useScenarioActions][FLOW] updateScenarioStatus failed", {
							id: id,
							newStatus: newStatus,
							error: error,
						});
						console.error("updateStatus failed", error);
						// Status changes mutate server state, so keep the shared error
						// contract here without offering a retry action.
						withError(error, null, {
							fallbackMessage: "Scenario status update failed.",
						});
					}),
			);
		},
		[
			dispatch,
			localScenario,
			showDialog,
			fetchScenarioByIdOrUuid,
			handleBackToList,
			trackEvent,
			withError,
		],
	);

	// EXPORT
	const exportScenarioAction = useCallback(
		async ({ includePhotos = false } = {}) => {
			debugLog(3, "[useScenarioActions][ACTION] exportScenarioAction", {
				includePhotos: includePhotos,
				localScenario: localScenario,
			});
			debugLog(2, "[useScenarioActions][FLOW] exportScenario started", {
				includePhotos: includePhotos,
			});

			let source = localScenario ?? {};
			const itemsMissingPhoto =
				Array.isArray(source?.baseline?.baseline_items) &&
				source.baseline.baseline_items.some((it) => it?.photo == null || it?.thumbnail == null);
			const configsMissingPhoto =
				Array.isArray(source?.baseline?.baseline_configs) &&
				source.baseline.baseline_configs.some((cfg) => cfg?.photo == null || cfg?.thumbnail == null);
			const needsPhotoFetch = includePhotos && (itemsMissingPhoto || configsMissingPhoto);

			// If user checked the box, grab a fresh baseline (with photos) without touching Redux
			if (needsPhotoFetch) {
				try {
					const idOrUuid = localScenario?.id ?? localScenario?.uuid;
					source = await fetchScenarioForExport(idOrUuid, { includePhotos: true });
				} catch (err) {
					errorLog(2, "[useScenarioActions][FLOW] fetchScenarioForExport failed", {
						err: String(err),
					});
					showDialog({
						type: "error",
						title: "Error",
						message: [
							"Could not load photo data for export.",
							"Please try again, or export without photos.",
						],
					});
					return;
				}
			}

			// keep only the essential objects
			// photo and thumbnails columns are not included as of 7/1/2025
			const thisScenario = Object.keys(source)
				.filter(
					(key) =>
						key.substring(0, 4) !== "cfg_" &&
						key.substring(0, 9) !== "baseline_" &&
						key !== "BL_LOOKUPS" &&
						key !== "prefCategory",
				)
				.reduce((obj, key) => {
					obj[key] = source[key];
					return obj;
				}, {});

			// Nullify photo and thumbnail in baseline_item_configs
			if (Array.isArray(thisScenario.baseline.baseline_items)) {
				thisScenario.baseline.baseline_items = thisScenario.baseline.baseline_items.map((item) => ({
					...item,
					photo: includePhotos ? (item.photo ?? null) : null,
					thumbnail: includePhotos ? (item.thumbnail ?? null) : null,
				}));
			}

			// Nullify photo and thumbnail in baseline_configs
			if (Array.isArray(thisScenario.baseline.baseline_configs)) {
				thisScenario.baseline.baseline_configs = thisScenario.baseline.baseline_configs.map(
					(config) => ({
						...config,
						photo: includePhotos ? (config.photo ?? null) : null,
						thumbnail: includePhotos ? (config.thumbnail ?? null) : null,
					}),
				);
			}

			debugLog(4, "[useScenarioActions][PROCESS] exportScenarioAction cleaned scenario", {
				thisScenario: thisScenario,
			});

			exportJSON(thisScenario, `Scenario_${localScenario.id}_${Session().loginUser.email}`);

			showDialog({
				title: "Successful",
				message: [`The data for Scenario (#${localScenario.id}) was exported successfully.`],
			});

			trackEvent({
				event: `Scenario (#${localScenario.id}) was exported`,
				props: null,
			});
		},
		[localScenario, showDialog, trackEvent, fetchScenarioForExport],
	);

	// DUMP
	const dumpScenarioAction = useCallback(() => {
		debugLog(3, "[useScenarioActions][ACTION] dumpScenarioAction", { localScenario: localScenario });
		debugLog(2, "[useScenarioActions][FLOW] dumpOneScenario started", { id: localScenario?.id });

		trackPromise(
			dispatch(dumpOneScenario(localScenario.id))
				.unwrap()
				.then((response) => {
					debugLog(3, "[useScenarioActions][STATE] scenario data dumped", { response: response });

					// Add XLSX export logic here if needed
					// Output in XLSX format
					let finalO = {};
					for (const key in response) {
						finalO[key] = response[key];
					}
					exportSpreadsheet(finalO, "xlsx");
					showDialog({
						title: "Successful",
						message: [`The data for Scenario (#${localScenario.id}) was dumped successfully.`],
					});

					trackEvent({
						event: `Scenario data (#${localScenario.id}) was dumped`,
						props: null,
					});
				})

				.catch((error) => {
					errorLog(2, "[useScenarioActions][FLOW] dumpOneScenario failed", {
						id: localScenario?.id,
						error: error,
					});
					console.error("dumpOneScenario failed", error);
					showDialog({
						title: "Error",
						message: ["The data dump failed!", "Please try again."],
						type: "error",
					});
				}),
		);
	}, [dispatch, localScenario, showDialog, trackEvent]);

	// IMPORT
	const importScenarioAction = useCallback(
		(data) => {
			debugLog(3, "[useScenarioActions][ACTION] importScenarioAction", { data: data });
			debugLog(2, "[useScenarioActions][FLOW] importScenario started", {});

			trackPromise(
				dispatch(importBaseline({ data: data.baseline }))
					.unwrap()
					.then((baselineRes) => {
						debugLog(3, "[useScenarioActions][STATE] baseline imported for scenario", {
							baselineId: baselineRes.id,
							filename: data.baseline.filename,
						});

						// Keep the imported payload immutable here so retries reuse a
						// predictable object shape and tests can assert the handoff cleanly.
						const scenarioImportData = {
							...data,
							baseline_id: baselineRes.id,
						};
						return dispatch(importScenario({ data: scenarioImportData })).unwrap();
					})
					.then((scenarioRes) => {
						debugLog(3, "[useScenarioActions][STATE] scenario imported", {
							scenarioId: scenarioRes.id,
						});

						showDialog(
							{
								title: "Successful",
								message: [`The Scenario (#${scenarioRes.id}) was imported successfully.`],
							},
							() => handleBackToList && handleBackToList(),
						);
					})
					.catch((error) => {
						errorLog(2, "[useScenarioActions][FLOW] importScenario failed", {
							error: error,
						});
						console.error("importScenario failed", error);
						// Scenario import runs in two stages (baseline then scenario), so
						// retries are intentionally disabled here; a response loss after
						// the Baseline step could otherwise create duplicate imported rows.
						withError(error, null, {
							fallbackMessage: "Scenario import failed.",
						});
					}),
			);
		},
		[dispatch, handleBackToList, showDialog, withError],
	);

	return {
		createScenarioAction,
		updateScenarioAction,
		deleteScenarioAction,
		copyScenarioAction,
		updateStatusAction,
		exportScenarioAction,
		importScenarioAction,
		dumpScenarioAction,
	};
}
